import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingService } from './services/loading.service';

@Component({
  standalone: true,
  imports: [RouterModule, CommonModule, MatProgressSpinnerModule],
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>

    <!-- @@ AI-COMMENT: Custom Loan-themed loading overlay with shimmering gold coin animation -->
    @if (loadingService.loading$ | async) {
      <div class="loan-loader-overlay">
        <div class="loader-container">
          <div class="loan-coin">
            <div class="coin-front">
              <span class="currency-symbol">₹</span>
            </div>
            <div class="coin-back">
              <span class="percentage-symbol">%</span>
            </div>
          </div>
          <div class="loader-text">Processing your request...</div>
          <div class="shimmer-line"></div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      /* @@ AI-COMMENT: Glassmorphism loading overlay with backdrop blur */
      .loan-loader-overlay {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        transition: all 0.3s ease;
      }

      /* @@ AI-COMMENT: Centered container for the animation and text */
      .loader-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.5rem;
        padding: 3rem;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 24px;
        box-shadow:
          0 20px 40px rgba(0, 0, 0, 0.05),
          inset 0 0 0 1px rgba(255, 255, 255, 0.5);
      }

      /* @@ AI-COMMENT: 3D flipping gold coin animation representing finance and interest */
      .loan-coin {
        width: 80px;
        height: 80px;
        position: relative;
        transform-style: preserve-3d;
        animation: coinFlip 3s infinite cubic-bezier(0.645, 0.045, 0.355, 1);
      }

      .coin-front,
      .coin-back {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 2rem;
        font-weight: 800;
        backface-visibility: hidden;
        border: 4px solid #fcd34d;
        box-shadow: 0 0 20px rgba(245, 158, 11, 0.3);
      }

      .coin-front {
        background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        color: #78350f;
      }

      .coin-back {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: white;
        transform: rotateY(180deg);
        border-color: #34d399;
      }

      @keyframes coinFlip {
        0% {
          transform: rotateY(0) scale(1);
        }
        40% {
          transform: rotateY(180deg) scale(1.1);
        }
        50% {
          transform: rotateY(180deg) scale(1.1);
        }
        90% {
          transform: rotateY(360deg) scale(1);
        }
        100% {
          transform: rotateY(360deg) scale(1);
        }
      }

      /* @@ AI-COMMENT: Modern typography for the loader status text */
      .loader-text {
        font-family: 'DM Sans', sans-serif;
        font-size: 1.1rem;
        font-weight: 600;
        color: #1e293b;
        letter-spacing: -0.01em;
      }

      /* @@ AI-COMMENT: Subtle shimmering line under the text */
      .shimmer-line {
        width: 120px;
        height: 3px;
        background: #e2e8f0;
        border-radius: 10px;
        position: relative;
        overflow: hidden;
      }

      .shimmer-line::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, #3b82f6, transparent);
        animation: shimmer 1.5s infinite linear;
      }

      @keyframes shimmer {
        100% {
          left: 100%;
        }
      }
    `,
  ],
})
export class AppComponent {
  loadingService = inject(LoadingService);

  constructor() {
    console.log('AppComponent: Application bootstrapped and started');
  }
}
